import React, { useEffect, useRef, useState } from 'react'

import { Button, Select, Tag, Typography, type SelectProps } from 'antd'
import classNames from 'classnames'
import { useOnClickTag } from 'features/common/hooks/useOnClickTag'
import { filesPreviewActions } from 'features/files-preview/model/slice/filesPreviewSlice'
import { useAppDispatch } from 'shared/config/store'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

interface TagsSelectorProps {
  value?: Array<{ id: string; value: string; label?: string }>;
  onSelect?: (value: string[]) => void;
  onChange?: (value: string[]) => void;
  options?: SelectProps['options'];
  placeholder?: string;
  onCreateTag?: (value: string) => void;
  className?: string
  classNameTagsContainer?: string
  createTagBtnStyle?: React.CSSProperties
  addTagBtnStyle?: React.CSSProperties
  disabled?: boolean
  renderCustomPlaceholderContent?: (onClick: () => void) => React.ReactNode
  showBorder?: boolean
  mode?: 'select-only' | 'select-create'
  isPublicLink?: boolean
  }
const { Text } = Typography

export const TagsSelector: React.FC<TagsSelectorProps> = ({
  value = [],
  onSelect,
  onChange,
  onCreateTag,
  options = [],
  placeholder = 'Add tags',
  className,
  classNameTagsContainer,
  createTagBtnStyle,
  addTagBtnStyle = {},
  disabled = false,
  renderCustomPlaceholderContent,
  showBorder = true,
  mode: tagsMode,
  isPublicLink = false
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const tagsSelectRef = useRef<any>(null)
  const { handleTagClick } = useOnClickTag()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  useEffect(() => {
    const GlobalStyles = `
    .ant-select-dropdown{
      background: ${({ showBorder }) => showBorder ? 'rgba(20, 20, 20, 1)' : 'transparent'} !important;
    }
    `

    const styleElement = document.createElement('style')
    styleElement.setAttribute('data-tags-selector-instance', 'true')
    styleElement.textContent = GlobalStyles
    document.head.appendChild(styleElement)

    return () => {
      if (styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement)
      }
    }
  }, [])


  const handleCreate = (searchValue: string) => {
    onCreateTag && onCreateTag(searchValue)
    setSearchValue('')
    tagsSelectRef.current?.blur()
  }

  const handleChange = (changes: string[]) => {
    onChange && onChange(changes)
  }

  const isTagAlreadySelected = (searchText: string) => {
    const normalizedSearch = searchText.toLowerCase().trim()
    return value.some(item => {
      const itemLabel = (item.label || item.value).toLowerCase().trim()
      return itemLabel === normalizedSearch
    })
  }

  if (!isEditing && value.length === 0) {
    if (renderCustomPlaceholderContent) {
      return renderCustomPlaceholderContent(() => setIsEditing(true))
    }

    return (
      <SButton type="link"
        style={{ color: 'rgba(0, 0, 0, 0.45)', paddingLeft: '0px', ...addTagBtnStyle }}
        onClick={() => !disabled && setIsEditing(true)}
        disabled={disabled}
      >
        Add tags
      </SButton>
    )
  }

  return (
    isEditing ? (
      <SSelect
        onFocus={() => !disabled && setIsEditing(true)}
        showArrow={false}
        className={`${className} tags-selector-custom`}
        mode="multiple"
        style={{ width: '100%' }}
        placeholder={placeholder}
        tagRender={(props) => {
          const { value: tagValue, closable, onClose } = props
          const tag = value.find(item => item.id === tagValue)

          return (
            <STag
              // style={{ border: '1px solid #424242', backgroundColor: 'rgba(255, 255, 255, 0.12)', cursor: 'pointer' }}
              closable={!isEditing ? false : closable}
              bordered={!isEditing}
              onClose={(e) => {
                e.stopPropagation()
                onClose(e)
              }}
              onClick={(e) => {
                e.stopPropagation()
                if(isPublicLink) return
                if (tag) {
                  handleTagClick(tag)
                  dispatch(filesPreviewActions.resetState())
                }
              }}
            >
              <STagText>
                {tag?.label || tag?.value || tagValue}
              </STagText>
            </STag>
          )
        }}
        onSearch={(value) => {
          setSearchValue(value)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && searchValue) {
            const hasExactMatch = options.some(
              (opt) =>
                (typeof opt.label === 'string' ? opt.label : '')
                  .toLowerCase()
                  .trim() === searchValue.toLowerCase().trim()
            )

            const isAlreadySelected = isTagAlreadySelected(searchValue)

            if (tagsMode === 'select-create') {
              if (!hasExactMatch && !isAlreadySelected) {
                e.preventDefault()
                handleCreate(searchValue)
              } else if (hasExactMatch && !isAlreadySelected) {
                setSearchValue('')
              } else if (isAlreadySelected) {
                e.preventDefault()
                setSearchValue('')
              }
            } else {
              if (isAlreadySelected) {
                e.preventDefault()
                setSearchValue('')
              }
            }
          }
        }}
        filterOption={(input, option) => {
          const label = typeof option?.label === 'string' ? option.label : String(option?.label || '')
          return label.toLowerCase().includes(input.toLowerCase())
        }}
        value={value}
        onChange={handleChange}
        onSelect={(selectedValue: string) => {
          const valueIds = value.map(item => item.id)
          const ids = [...valueIds, selectedValue]
          onSelect?.(ids)
          setSearchValue('')
          tagsSelectRef.current?.blur()
        }}
        ref={tagsSelectRef}
        options={options}
        onBlur={() => setIsEditing(false)}
        autoFocus={isEditing}
        disabled={disabled}
        searchValue={searchValue}
        autoClearSearchValue={false}
        notFoundContent={(() => {
          if (!searchValue) return null

          const hasSubstringMatch = options.some(
            (opt) =>
              (typeof opt.label === 'string' ? opt.label : String(opt?.label || ''))
                .toLowerCase()
                .includes(searchValue.toLowerCase())
          )

          if (tagsMode === 'select-create') {
            const hasExactMatch = options.some(
              (opt) =>
                (typeof opt.label === 'string' ? opt.label : '')
                  .toLowerCase()
                  .trim() === searchValue.toLowerCase().trim()
            )
            const alreadySelected = isTagAlreadySelected(searchValue)
            if (!hasSubstringMatch && !hasExactMatch && !alreadySelected) {
              return (
                <SCreateTagButton
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleCreate(searchValue)}
                  style={createTagBtnStyle}
                >
                  + Create
                  &quot;
                  {searchValue}
&quot;
                </SCreateTagButton>
              )
            }
            return null
          }

          // select-only mode: show "No options" when no matches
          if (!hasSubstringMatch) {
            return (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '7px'  }}>
                <Text type="secondary"
                  style={{ fontWeight: '300', fontStyle: 'italic' }}
                >
                  {t('l_notifications_tagNotFound')}
                </Text>
              </div>
            )
          }
          return null
        })()}
        dropdownRender={(menu) => {
          const hasExactMatch = options.some(
            (opt) =>
              (typeof opt.label === 'string' ? opt.label : '')
                .toLowerCase()
                .trim() === searchValue.toLowerCase().trim()
          )
          const hasSubstringMatch = options.some(
            (opt) =>
              (typeof opt.label === 'string' ? opt.label : String(opt?.label || ''))
                .toLowerCase()
                .includes(searchValue.toLowerCase())
          )

          const isAlreadySelected = isTagAlreadySelected(searchValue)
          return (
            <>
              {menu}

              {searchValue && (
                <>
                  {tagsMode === 'select-create' && hasSubstringMatch && !hasExactMatch && !isAlreadySelected && (
                    <SCreateTagButton
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleCreate(searchValue)}
                      style={createTagBtnStyle}
                    >
                    + Create
                    &quot;
                      {searchValue}
                    &quot;
                    </SCreateTagButton>
                  )}
                </>
              )}
            </>
          )
        }}
      />
    ) : (
      <STagsContainer className={classNames(classNameTagsContainer, className, 'tags-selector-container')}
        onClick={() => !disabled && setIsEditing(true)}
      >
        {value.map((item) => (
          <STag
            style={{ border: `1px solid ${!showBorder ? '#d9d9d9' : '#424242'}`, backgroundColor: 'rgba(255, 255, 255, 0.04)', cursor: 'pointer' }}
            key={item.id}
            closable={false}
            onClick={(e) => {
              if (disabled) {
                return
              }

              e.stopPropagation()
              if(isPublicLink) return
              handleTagClick(item)
              dispatch(filesPreviewActions.resetState())
            }}
          >
            <STagText>
              {item.label || item.value}
            </STagText>
          </STag>
        ))}
      </STagsContainer>
    )
  )
}


const STagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;

  &:hover{
    cursor: pointer;
    background: var(--color-gray-100);
    border-radius: 4px;
  }
`

const SSelect = styled(Select)`
  .ant-select-selector {
    padding: 2px 0px 2px 4px !important;
    /* background: transparent !important; */
    border: none !important;
    
    box-shadow: 0 0 0 2px var(--color-primary) !important;
    gap: 4px !important;

    .ant-select-selection-overflow{
      gap: 4px !important;

      & span {
        max-width: 104px !important;
      }
    }
  }

  &.ant-select-disabled {
    .ant-select-selector {
      background: initial!important;
    }
  }
`

const SButton = styled(Button)`
  color: rgba(0, 0, 0, 0.45);
  margin-left: 3px;
  /* margin-top: 32px; */
  margin-bottom: 2px;
  height: 32px;
`

const SCreateTagButton = styled.div`
  display: flex;
  align-items: center;
  padding: 5px 10px;
  margin: 4px;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s;
  color: var(--color-gray-600);
`

const STagText = styled(Text)`
  display: block;
  max-width: 60px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const STag = styled(Tag)`
  display: flex;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-inline-end: 0%;

  & .ant-tag-close-icon{
    margin-left: 4px !important;
  }

`
